import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PowerOff } from 'lucide-react';
import apiService from '../../services/api';
import ConfirmModal from './ConfirmModal';
import { useTheme } from '../../contexts/ThemeContext';

const SettingsTab = () => {
  const navigate = useNavigate();
  // Tema é usado apenas via classes dark: do Tailwind
  useTheme(); // Hook necessário para acesso ao tema
  const [blockedKeywords, setBlockedKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [blockedHistory, setBlockedHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [stats, setStats] = useState({});
  const [deletingInstance, setDeletingInstance] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blockOnlyCrmContacts, setBlockOnlyCrmContacts] = useState(true);
  const [blockBehaviorLoading, setBlockBehaviorLoading] = useState(false);

  // Carregar configuração de comportamento de bloqueio
  const loadBlockBehavior = async () => {
    try {
      const response = await fetch('/api/crm/settings/block-behavior', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBlockOnlyCrmContacts(data.blockOnlyCrmContacts);
      } else {
        // Erro ao carregar configuração de comportamento de bloqueio
      }
    } catch (error) {
      // Erro ao carregar configuração de comportamento
    }
  };

  // Atualizar configuração de comportamento de bloqueio
  const updateBlockBehavior = async (newValue) => {
    try {
      setBlockBehaviorLoading(true);
      const response = await fetch('/api/crm/settings/block-behavior', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ blockOnlyCrmContacts: newValue })
      });

      if (response.ok) {
        setBlockOnlyCrmContacts(newValue);
        toast.success('Configuração de bloqueio atualizada com sucesso');
      } else {
        toast.error('Erro ao atualizar configuração de bloqueio');
      }
    } catch (error) {
      toast.error('Erro ao atualizar configuração de bloqueio');
    } finally {
      setBlockBehaviorLoading(false);
    }
  };

  // Carregar palavras-chave bloqueadas
  const loadBlockedKeywords = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/crm/settings/blocked-keywords', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBlockedKeywords(data.blockedKeywords || []);
      } else {
        toast.error('Erro ao carregar palavras-chave bloqueadas');
      }
    } catch (error) {
      toast.error('Erro ao carregar palavras-chave bloqueadas');
    } finally {
      setLoading(false);
    }
  };

  // Carregar histórico de contatos bloqueados
  const loadBlockedHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetch('/api/crm/contacts/blocked-history?limit=10', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBlockedHistory(data.blockedContacts || []);
        setStats(data.stats || {});
      } else {
        toast.error('Erro ao carregar histórico de bloqueios');
      }
    } catch (error) {
      toast.error('Erro ao carregar histórico de bloqueios');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Adicionar nova palavra-chave
  const addKeyword = async () => {
    if (!newKeyword.trim()) {
      toast.error('Digite uma palavra-chave válida');
      return;
    }

    try {
      const response = await fetch('/api/crm/settings/blocked-keywords', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ keyword: newKeyword.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setBlockedKeywords(data.blockedKeywords);
        setNewKeyword('');
        toast.success('Palavra-chave adicionada com sucesso');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Erro ao adicionar palavra-chave');
      }
    } catch (error) {
      toast.error('Erro ao adicionar palavra-chave');
    }
  };

  // Remover palavra-chave
  const removeKeyword = async (keyword) => {
    if (!window.confirm(`Tem certeza que deseja remover a palavra-chave "${keyword}"?`)) {
      return;
    }

    try {
      const response = await apiService.request(`/crm/settings/blocked-keywords/${encodeURIComponent(keyword)}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        setBlockedKeywords(data.blockedKeywords);
        toast.success('Palavra-chave removida com sucesso');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Erro ao remover palavra-chave');
      }
    } catch (error) {
      toast.error('Erro ao remover palavra-chave');
    }
  };

  // Reativar contato bloqueado
  const unblockContact = async (contactId, contactName) => {
    if (!window.confirm(`Tem certeza que deseja reativar o contato "${contactName}"?`)) {
      return;
    }

    try {
      const response = await apiService.request(`/crm/contacts/${contactId}/unblock`, {
        method: 'PUT',
        body: JSON.stringify({ reason: 'Reativado manualmente pelo usuário' })
      });

      if (response.ok) {
        toast.success('Contato reativado com sucesso');
        loadBlockedHistory(); // Recarregar histórico
      } else {
        const error = await response.json();
        toast.error(error.error || 'Erro ao reativar contato');
      }
    } catch (error) {
      toast.error('Erro ao reativar contato');
    }
  };

  // Abrir modal de confirmação
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  // Excluir instância do WhatsApp
  const deleteInstance = async () => {
    setShowDeleteModal(false);
    setDeletingInstance(true);
    
    try {
      // Nome fixo da instância CRM (deve ser consistente com outras partes do sistema)
      const instanceName = 'crm_principal';
      
      if (!instanceName) {
        toast.error('Nome da instância não encontrado');
        return;
      }

      const response = await apiService.deleteEvolutionInstance(instanceName);
      
      if (response.success) {
        toast.success('Instância excluída com sucesso! Redirecionando para configuração...', 'success');
        // Aguardar um pouco para mostrar a notificação e depois redirecionar
        setTimeout(() => {
          navigate('/crm-conectar');
        }, 1500);
      } else {
        toast.error('Erro ao excluir instância: ' + (response.message || 'Erro desconhecido'));
        setDeletingInstance(false);
      }
    } catch (error) {
      toast.error('Erro ao excluir instância: ' + (error.message || 'Erro desconhecido'));
      setDeletingInstance(false);
    }
    // Não definir setDeletingInstance(false) no finally para manter o loading até o redirecionamento
  };

  useEffect(() => {
    loadBlockedKeywords();
    loadBlockedHistory();
    loadBlockBehavior();
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Configurações do CRM</h2>

      {/* Seção de Palavras-chave Bloqueadas */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">
          Palavras-chave de Bloqueio Automático
        </h3>
        <p className="text-gray-600 mb-4">
          Quando um contato responder com uma dessas palavras, será automaticamente marcado como "ignorado" 
          e excluído de futuras campanhas.
        </p>

        {/* Adicionar nova palavra-chave */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Digite uma palavra-chave (ex: SAIR, PARAR)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
          />
          <button
            onClick={addKeyword}
            disabled={!newKeyword.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Adicionar
          </button>
        </div>

        {/* Lista de palavras-chave */}
        {loading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {blockedKeywords.map((keyword, index) => (
              <div
                key={index}
                className="flex items-center bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm"
              >
                <span>{keyword}</span>
                <button
                  onClick={() => removeKeyword(keyword)}
                  className="ml-2 text-red-600 hover:text-red-800 font-bold"
                  title="Remover palavra-chave"
                >
                  ×
                </button>
              </div>
            ))}
            {blockedKeywords.length === 0 && (
              <p className="text-gray-500 italic">Nenhuma palavra-chave configurada</p>
            )}
          </div>
        )}
      </div>

      {/* Seção de Comportamento de Bloqueio */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">
          Comportamento de Bloqueio
        </h3>
        <p className="text-gray-600 mb-4">
          Configure quando as palavras-chave de bloqueio devem ser aplicadas.
        </p>

        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <input
              type="radio"
              id="block-crm-only"
              name="blockBehavior"
              checked={blockOnlyCrmContacts}
              onChange={() => updateBlockBehavior(true)}
              disabled={blockBehaviorLoading}
              className="mt-1"
            />
            <div className="flex-1">
              <label htmlFor="block-crm-only" className="font-medium text-gray-700 cursor-pointer">
                Bloquear apenas contatos com interação CRM (Recomendado)
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Só bloqueia contatos que já receberam mensagens do CRM ou estão na lista de contatos. 
                Ideal quando você usa IA no mesmo número para conversas normais.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="radio"
              id="block-all"
              name="blockBehavior"
              checked={!blockOnlyCrmContacts}
              onChange={() => updateBlockBehavior(false)}
              disabled={blockBehaviorLoading}
              className="mt-1"
            />
            <div className="flex-1">
              <label htmlFor="block-all" className="font-medium text-gray-700 cursor-pointer">
                Bloquear todos os contatos
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Bloqueia qualquer contato que enviar palavras-chave, mesmo sem interação prévia com o CRM.
              </p>
            </div>
          </div>
        </div>

        {blockBehaviorLoading && (
          <div className="mt-4 flex items-center justify-center">
            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
            <span className="text-sm text-gray-600">Atualizando configuração...</span>
          </div>
        )}
      </div>

      {/* Seção de Estatísticas */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Estatísticas de Bloqueio</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-gray-800">{stats.totalBlocked || 0}</div>
            <div className="text-sm text-gray-600">Total Bloqueados</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-yellow-800">{stats.blockedThisWeek || 0}</div>
            <div className="text-sm text-yellow-600">Esta Semana</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-800">{stats.blockedThisMonth || 0}</div>
            <div className="text-sm text-red-600">Este Mês</div>
          </div>
        </div>
      </div>

      {/* Seção de Histórico de Bloqueios */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-700">Histórico de Contatos Bloqueados</h3>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            {showHistory ? 'Ocultar' : 'Mostrar'} Histórico
          </button>
        </div>

        {showHistory && (
          <>
            {historyLoading ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                <p className="mt-2 text-gray-600">Carregando histórico...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blockedHistory.map((contact) => (
                  <div key={contact.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{contact.nome}</div>
                      <div className="text-sm text-gray-600">{contact.number}</div>
                      <div className="text-xs text-red-600 mt-1">
                        Bloqueado em: {new Date(contact.blocked_at).toLocaleString('pt-BR')}
                      </div>
                      <div className="text-xs text-gray-500">
                        Motivo: {contact.blocked_reason}
                      </div>
                    </div>
                    <button
                      onClick={() => unblockContact(contact.id, contact.nome)}
                      className="px-3 py-1 bg-green-500 text-white text-sm rounded-md hover:bg-green-600"
                    >
                      Reativar
                    </button>
                  </div>
                ))}
                {blockedHistory.length === 0 && (
                  <p className="text-gray-500 italic text-center py-4">
                    Nenhum contato bloqueado encontrado
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Seção de Gerenciamento de Instância */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Gerenciamento de Instância</h3>
        <p className="text-gray-600 mb-4">
          Gerencie a conexão do seu WhatsApp com o CRM.
        </p>
        
        <div className="border border-red-200 rounded-lg p-4 bg-red-50">
          <h4 className="text-md font-medium text-red-800 mb-2">Zona de Perigo</h4>
          <p className="text-red-700 text-sm mb-4">
            Desconecte seu número vínculado ao CRM. Esta ação irá excluir a instância do WhatsApp e você precisará reconectar.
          </p>
          <button
            onClick={handleDeleteClick}
            disabled={deletingInstance}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {deletingInstance ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Excluindo Instância...
              </>
            ) : (
              <>
                <PowerOff className="w-4 h-4 mr-2" />
                Excluir a Instância
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteInstance}
        title="Excluir Instância do WhatsApp"
        message="Tem certeza que deseja excluir a instância do WhatsApp? Esta ação não pode ser desfeita e você precisará reconectar o WhatsApp."
        confirmText="Excluir Instância"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default SettingsTab;